import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRightLeft, Search, Check, Undo2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Iter 151: sposta le ore di una commessa (periodo / rapportini / singole righe) in un'altra, poi chiede se cestinare l'origine
export function SpostaOreCard() {
  const [origine, setOrigine] = useState("");
  const [dest, setDest] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const body = () => ({ origine: origine.trim(), destinazione: dest.trim(), date_from: from || null, date_to: to || null });

  const runPreview = async () => {
    if (!origine.trim() || !dest.trim()) return toast.error("Indica commessa di origine e di destinazione");
    setBusy(true); setPreview(null); setResult(null);
    try {
      const { data } = await api.post("/admin/bulk/sposta-ore/anteprima", body());
      setPreview(data);
      setSelected(new Set(data.items.flatMap((i) => i.righe.map((r) => r.key))));
      if (!data.count) toast.info("Nessuna ora trovata per quella commessa nel periodo");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const toggleRiga = (key) => setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleRap = (item) => setSelected((s) => {
    const n = new Set(s); const all = item.righe.every((r) => n.has(r.key));
    item.righe.forEach((r) => (all ? n.delete(r.key) : n.add(r.key))); return n;
  });
  const oreSel = preview ? preview.items.flatMap((i) => i.righe).filter((r) => selected.has(r.key)).reduce((a, r) => a + r.ore_tot, 0) : 0;

  const apply = async () => {
    if (!selected.size) return toast.error("Seleziona almeno una riga");
    if (!preview.destinazione.esiste) return toast.error(`La commessa ${preview.destinazione.codice} non esiste: creala prima in Commesse`);
    if (!window.confirm(`Spostare ${oreSel.toFixed(1)} ore (${selected.size} righe) da ${preview.origine.codice} a ${preview.destinazione.codice}?\n\nViene salvata una copia di sicurezza: potrai annullare.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bulk/sposta-ore/applica", { ...body(), righe: [...selected] });
      setResult(data); setPreview(null);
      toast.success(`${data.ore_spostate} ore spostate in ${data.updated} rapportini`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    if (!result?.batch_id || !window.confirm("Ripristinare le ore com'erano prima dello spostamento?")) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/bulk/annulla/${result.batch_id}`); toast.success(`${data.restored} rapportini ripristinati`); setResult(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const deleteOrigine = async () => {
    if (!result?.origine?.id || !window.confirm(`Mettere nel Cestino la commessa ${result.origine.codice}? (recuperabile dal Cestino)`)) return;
    setBusy(true);
    try { await api.delete(`/commesse/${result.origine.id}`); toast.success(`Commessa ${result.origine.codice} spostata nel Cestino`); setResult({ ...result, origine: { ...result.origine, eliminata: true } }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 space-y-4 border-slate-300" data-testid="sposta-ore-card">
      <div className="font-heading font-bold tracking-tight flex items-center gap-2"><ArrowRightLeft className="h-4 w-4" /> Sposta ore tra commesse</div>
      <p className="text-xs text-slate-500">Tutte le ore di una commessa in un periodo, oppure solo alcuni rapportini o singole righe, passano a un'altra commessa. Alla fine puoi cestinare la commessa svuotata.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1"><Label className="text-xs font-bold">Da commessa</Label><Input value={origine} onChange={(e) => setOrigine(e.target.value.toUpperCase())} placeholder="EG-50-226-OFF" className="font-mono" data-testid="sposta-ore-origine" /></div>
        <div className="space-y-1"><Label className="text-xs font-bold">A commessa</Label><Input value={dest} onChange={(e) => setDest(e.target.value.toUpperCase())} placeholder="EG-50-226" className="font-mono" data-testid="sposta-ore-dest" /></div>
        <div className="space-y-1"><Label className="text-xs font-bold">Dal (opzionale)</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="sposta-ore-from" /></div>
        <div className="space-y-1"><Label className="text-xs font-bold">Al (opzionale)</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="sposta-ore-to" /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={runPreview} disabled={busy} data-testid="sposta-ore-preview"><Search className="h-4 w-4 mr-1.5" /> Anteprima</Button>
        {preview?.count > 0 && (
          <Button onClick={apply} disabled={busy || !selected.size} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="sposta-ore-apply"><Check className="h-4 w-4 mr-1.5" /> Sposta {oreSel.toFixed(1)} ore ({selected.size} righe)</Button>
        )}
      </div>
      {preview && (
        <div className="space-y-2" data-testid="sposta-ore-preview-box">
          <div className="text-xs flex flex-wrap gap-3">
            <span className={preview.origine.esiste ? "text-slate-600" : "text-amber-700 font-bold"}>Origine <b className="font-mono">{preview.origine.codice}</b> {preview.origine.esiste ? `· ${preview.origine.cliente || "—"} · ${preview.origine.ore_totali_storiche} ore totali storiche` : "· non in anagrafica (solo nelle righe)"}</span>
            <span className={preview.destinazione.esiste ? "text-slate-600" : "text-red-700 font-bold"}>Destinazione <b className="font-mono">{preview.destinazione.codice}</b> {preview.destinazione.esiste ? `· ${preview.destinazione.cliente || "—"}` : "· NON ESISTE: creala prima in Commesse"}</span>
          </div>
          <div className="border rounded-md divide-y max-h-96 overflow-y-auto text-sm">
            {preview.items.length === 0 && <div className="p-3 text-slate-500">Nessuna riga trovata.</div>}
            {preview.items.map((it) => (
              <div key={it.id} className="p-2" data-testid={`sposta-ore-rap-${it.id}`}>
                <label className="flex items-center gap-2 font-bold cursor-pointer">
                  <input type="checkbox" checked={it.righe.every((r) => selected.has(r.key))} onChange={() => toggleRap(it)} className="h-4 w-4" data-testid={`sposta-ore-rap-check-${it.id}`} />
                  N° {it.numero} · {it.data} · {it.created_by_name} · {it.ore} ore {it.vidimato && <span className="text-[10px] px-1 rounded bg-emerald-100 text-emerald-800">vidimato</span>}
                </label>
                <div className="pl-6 mt-1 grid sm:grid-cols-2 gap-x-4">
                  {it.righe.map((r) => (
                    <label key={r.key} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer py-0.5">
                      <input type="checkbox" checked={selected.has(r.key)} onChange={() => toggleRiga(r.key)} className="h-3.5 w-3.5" data-testid={`sposta-ore-riga-${r.key.replace(/\|/g, "-")}`} />
                      {r.data} · {r.dipendente} · <b>{r.ore_tot} h</b>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {result && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm space-y-2" data-testid="sposta-ore-result">
          <div><b>{result.ore_spostate} ore</b> spostate in {result.updated} rapportini verso <b className="font-mono">{dest}</b>.</div>
          {result.origine.esiste && !result.origine.eliminata && (
            <div className="flex flex-wrap items-center gap-2">
              {result.origine.ore_residue > 0
                ? <span className="text-amber-800 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> La commessa {result.origine.codice} ha ancora {result.origine.ore_residue} ore in altri rapportini.</span>
                : <span>La commessa {result.origine.codice} ora non ha più ore.</span>}
              <Button size="sm" variant="outline" onClick={deleteOrigine} disabled={busy} className="border-red-300 text-red-700" data-testid="sposta-ore-delete-origine"><Trash2 className="h-4 w-4 mr-1" /> Cestina {result.origine.codice}</Button>
              <span className="text-xs text-slate-500">oppure lasciala com'è</span>
            </div>
          )}
          {result.origine.eliminata && <div className="text-slate-600">Commessa {result.origine.codice} nel Cestino.</div>}
          <Button size="sm" variant="outline" onClick={undo} disabled={busy} className="border-amber-400 text-amber-800" data-testid="sposta-ore-undo"><Undo2 className="h-4 w-4 mr-1" /> Annulla spostamento</Button>
        </div>
      )}
    </Card>
  );
}
